import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import apiService from '../../services/api.service';
import './ContactMessages.scss';

const ContactMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, unread: 0, read: 0, replied: 0 });
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });

    useEffect(() => {
        fetchMessages();
    }, [filter, pagination.current_page]);

    const fetchMessages = async () => {
        setLoading(true);
        try {
            const response = await apiService.get('/contact-messages', {
                status: filter,
                search: search,
                page: pagination.current_page,
                per_page: 15
            });
            if (response.success) {
                setMessages(response.messages.data || []);
                setStats(response.stats);
                setPagination({
                    current_page: response.messages.current_page,
                    last_page: response.messages.last_page
                });
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, current_page: 1 }));
        fetchMessages();
    };

    const handleViewMessage = async (message) => {
        try {
            const response = await apiService.get(`/contact-messages/${message.id}`);
            if (response.success) {
                setSelectedMessage(response.message);
                setShowModal(true);
                fetchMessages();
            }
        } catch (error) {
            console.error('Error fetching message:', error);
        }
    };

    const handleUpdateStatus = async (status) => {
        if (!selectedMessage) return;
        try {
            await apiService.put(`/contact-messages/${selectedMessage.id}`, { status });
            setSelectedMessage(prev => ({ ...prev, status }));
            fetchMessages();
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) return;
        try {
            await apiService.delete(`/contact-messages/${id}`);
            setShowModal(false);
            fetchMessages();
        } catch (error) {
            console.error('Error deleting message:', error);
        }
    };

    const [ipActionLoading, setIpActionLoading] = useState(false);

    const [showBlockedModal, setShowBlockedModal] = useState(false);
    const [blockedIps, setBlockedIps] = useState([]);
    const [blockedLoading, setBlockedLoading] = useState(false);
    const [manualIp, setManualIp] = useState('');

    const fetchBlockedIps = async () => {
        setBlockedLoading(true);
        try {
            const response = await apiService.get('/blocked-ips');
            setBlockedIps(response.data || []);
        } catch (error) {
            console.error('Error fetching blocked IPs:', error);
        } finally {
            setBlockedLoading(false);
        }
    };

    const openBlockedModal = () => {
        setShowBlockedModal(true);
        fetchBlockedIps();
    };

    const blockIp = async (ip, { askDelete = true } = {}) => {
        if (!ip) return false;
        if (!window.confirm(`Bloquer l'adresse IP ${ip} ?\nCette IP n'aura plus accès au site.`)) return false;
        const deleteMessages = askDelete && window.confirm('Supprimer aussi tous les messages envoyés depuis cette IP ?');

        setIpActionLoading(true);
        try {
            const response = await apiService.post('/blocked-ips', {
                ip_address: ip,
                delete_messages: deleteMessages,
            });
            alert(response.message || 'Adresse IP bloquée.');
            if (deleteMessages && selectedMessage?.ip_address === ip) {
                setShowModal(false);
            } else if (selectedMessage?.ip_address === ip) {
                setSelectedMessage(prev => ({ ...prev, is_ip_blocked: true }));
            }
            fetchMessages();
            if (showBlockedModal) fetchBlockedIps();
            return true;
        } catch (error) {
            alert(error.message || 'Erreur lors du blocage de l\'IP.');
            return false;
        } finally {
            setIpActionLoading(false);
        }
    };

    const unblockIp = async (ip) => {
        if (!ip || !window.confirm(`Débloquer l'adresse IP ${ip} ?`)) return;

        setIpActionLoading(true);
        try {
            await apiService.delete(`/blocked-ips/${encodeURIComponent(ip)}`);
            if (selectedMessage?.ip_address === ip) {
                setSelectedMessage(prev => ({ ...prev, is_ip_blocked: false }));
            }
            fetchMessages();
            if (showBlockedModal) fetchBlockedIps();
        } catch (error) {
            alert(error.message || 'Erreur lors du déblocage de l\'IP.');
        } finally {
            setIpActionLoading(false);
        }
    };

    const handleBlockIp = () => blockIp(selectedMessage?.ip_address);
    const handleUnblockIp = () => unblockIp(selectedMessage?.ip_address);

    const handleManualBlock = async (e) => {
        e.preventDefault();
        const ip = manualIp.trim();
        if (!ip) return;
        if (await blockIp(ip, { askDelete: false })) setManualIp('');
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'pending': { label: 'Non lu', className: 'pending' },
            'read': { label: 'Lu', className: 'read' },
            'replied': { label: 'Répondu', className: 'replied' },
            'archived': { label: 'Archivé', className: 'archived' }
        };
        const config = statusMap[status] || { label: status, className: 'default' };
        return <span className={`status-badge ${config.className}`}>{config.label}</span>;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        
        if (days === 0) {
            return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } else if (days === 1) {
            return 'Hier';
        } else if (days < 7) {
            return `Il y a ${days} jours`;
        }
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    };

    const formatFullDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="contact-messages-page">
            {/* Page Header */}
            <div className="page-header">
                <div className="page-header-content">
                    <h1>Messages de Contact</h1>
                    <p>Gérez les messages reçus via le formulaire de contact</p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card total">
                    <div className="stat-icon">
                        <i className="ph ph-envelope-simple"></i>
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{stats.total}</span>
                        <span className="stat-label">Total</span>
                    </div>
                </div>
                <div className="stat-card unread">
                    <div className="stat-icon">
                        <i className="ph ph-envelope"></i>
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{stats.unread}</span>
                        <span className="stat-label">Non lus</span>
                    </div>
                </div>
                <div className="stat-card read">
                    <div className="stat-icon">
                        <i className="ph ph-envelope-open"></i>
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{stats.read}</span>
                        <span className="stat-label">Lus</span>
                    </div>
                </div>
                <div className="stat-card replied">
                    <div className="stat-icon">
                        <i className="ph ph-check-circle"></i>
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{stats.replied}</span>
                        <span className="stat-label">Répondus</span>
                    </div>
                </div>
            </div>

            {/* Main Content Card */}
            <div className="messages-card">
                {/* Toolbar */}
                <div className="messages-toolbar">
                    <div className="filter-tabs">
                        <button 
                            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => { setFilter('all'); setPagination(prev => ({ ...prev, current_page: 1 })); }}
                        >
                            Tous
                        </button>
                        <button 
                            className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
                            onClick={() => { setFilter('pending'); setPagination(prev => ({ ...prev, current_page: 1 })); }}
                        >
                            Non lus
                            {stats.unread > 0 && <span className="badge">{stats.unread}</span>}
                        </button>
                        <button 
                            className={`filter-tab ${filter === 'read' ? 'active' : ''}`}
                            onClick={() => { setFilter('read'); setPagination(prev => ({ ...prev, current_page: 1 })); }}
                        >
                            Lus
                        </button>
                        <button 
                            className={`filter-tab ${filter === 'replied' ? 'active' : ''}`}
                            onClick={() => { setFilter('replied'); setPagination(prev => ({ ...prev, current_page: 1 })); }}
                        >
                            Répondus
                        </button>
                    </div>
                    <button type="button" className="blocked-ips-btn" onClick={openBlockedModal}>
                        <i className="ph ph-prohibit"></i>
                        IPs bloquées
                    </button>
                    <form onSubmit={handleSearch} className="search-form">
                        <div className="search-wrapper">
                            <i className="ph ph-magnifying-glass"></i>
                            <input
                                type="text"
                                placeholder="Rechercher..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </form>
                </div>

                {/* Messages List */}
                <div className="messages-list">
                    {loading ? (
                        <div className="loading-state">
                            <Spinner animation="border" variant="primary" />
                            <p>Chargement des messages...</p>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">
                                <i className="ph ph-chat-circle-dots"></i>
                            </div>
                            <h3>Aucun message</h3>
                            <p>Vous n'avez pas encore reçu de messages.</p>
                        </div>
                    ) : (
                        messages.map((message) => (
                            <div 
                                key={message.id} 
                                className={`message-item ${message.status === 'pending' ? 'unread' : ''}`}
                                onClick={() => handleViewMessage(message)}
                            >
                                <div className="message-avatar">
                                    {message.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="message-body">
                                    <div className="message-header">
                                        <span className="sender-name">{message.name}</span>
                                        <span className="message-time">{formatDate(message.created_at)}</span>
                                    </div>
                                    {message.ip_address && (
                                        <div className="message-ip">
                                            <i className="ph ph-globe"></i> {message.ip_address}
                                            {message.is_ip_blocked && <span className="ip-blocked-tag">Bloquée</span>}
                                        </div>
                                    )}
                                    <div className="message-subject">{message.subject}</div>
                                    <div className="message-preview">
                                        {message.message.length > 80 
                                            ? message.message.substring(0, 80) + '...' 
                                            : message.message}
                                    </div>
                                </div>
                                <div className="message-status">
                                    {getStatusBadge(message.status)}
                                </div>
                                {message.is_ip_blocked ? (
                                    <button
                                        className="row-ip-btn unblock"
                                        onClick={(e) => { e.stopPropagation(); unblockIp(message.ip_address); }}
                                        disabled={ipActionLoading}
                                        title={`Débloquer ${message.ip_address}`}
                                    >
                                        <i className="ph ph-lock-open"></i>
                                        <span>Débloquer</span>
                                    </button>
                                ) : (
                                    <button
                                        className="row-ip-btn block"
                                        onClick={(e) => { e.stopPropagation(); blockIp(message.ip_address); }}
                                        disabled={ipActionLoading || !message.ip_address}
                                        title={message.ip_address
                                            ? `Bloquer ${message.ip_address}`
                                            : 'IP non disponible (message reçu avant l\'activation du suivi IP)'}
                                    >
                                        <i className="ph ph-prohibit"></i>
                                        <span>Bloquer</span>
                                    </button>
                                )}
                                <button 
                                    className="delete-btn"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(message.id); }}
                                    title="Supprimer"
                                >
                                    <i className="ph ph-trash"></i>
                                </button>
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                    <div className="pagination-bar">
                        <button 
                            className="pagination-btn"
                            disabled={pagination.current_page === 1}
                            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                        >
                            <i className="ph ph-caret-left"></i>
                            Précédent
                        </button>
                        <span className="pagination-info">
                            Page {pagination.current_page} sur {pagination.last_page}
                        </span>
                        <button 
                            className="pagination-btn"
                            disabled={pagination.current_page === pagination.last_page}
                            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                        >
                            Suivant
                            <i className="ph ph-caret-right"></i>
                        </button>
                    </div>
                )}
            </div>

            {/* Message Detail Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="message-detail-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Détails du Message</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedMessage && (
                        <div className="message-detail">
                            <div className="detail-header">
                                <div className="sender-avatar">
                                    {selectedMessage.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="sender-info">
                                    <h4>{selectedMessage.name}</h4>
                                    <div className="contact-info">
                                        <span><i className="ph ph-envelope"></i>{selectedMessage.email}</span>
                                        {selectedMessage.phone && (
                                            <span><i className="ph ph-phone"></i>{selectedMessage.phone}</span>
                                        )}
                                    </div>
                                </div>
                                {getStatusBadge(selectedMessage.status)}
                            </div>

                            <div className="detail-section">
                                <label>Sujet</label>
                                <p className="subject-text">{selectedMessage.subject}</p>
                            </div>

                            <div className="detail-section">
                                <label>Message</label>
                                <div className="message-text">{selectedMessage.message}</div>
                            </div>

                            <div className="detail-section">
                                <label>Adresse IP</label>
                                <div className="ip-row">
                                    <div className="ip-info">
                                        <code className="ip-value">{selectedMessage.ip_address || 'Non disponible'}</code>
                                        {selectedMessage.is_ip_blocked && (
                                            <span className="ip-blocked-tag">
                                                <i className="ph ph-prohibit"></i> Bloquée
                                            </span>
                                        )}
                                    </div>
                                    {selectedMessage.ip_address && (
                                        selectedMessage.is_ip_blocked ? (
                                            <button
                                                className="ip-btn unblock"
                                                onClick={handleUnblockIp}
                                                disabled={ipActionLoading}
                                            >
                                                <i className="ph ph-lock-open"></i>
                                                Débloquer IP
                                            </button>
                                        ) : (
                                            <button
                                                className="ip-btn block"
                                                onClick={handleBlockIp}
                                                disabled={ipActionLoading}
                                            >
                                                <i className="ph ph-prohibit"></i>
                                                Bloquer IP
                                            </button>
                                        )
                                    )}
                                </div>
                                {selectedMessage.user_agent && (
                                    <p className="user-agent-text">{selectedMessage.user_agent}</p>
                                )}
                            </div>

                            <div className="detail-date">
                                <i className="ph ph-calendar"></i>
                                Reçu le {formatFullDate(selectedMessage.created_at)}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="modal-actions">
                        <button 
                            className="action-btn delete"
                            onClick={() => selectedMessage && handleDelete(selectedMessage.id)}
                        >
                            <i className="ph ph-trash"></i>
                            Supprimer
                        </button>
                        <div className="action-group">
                            {selectedMessage?.status !== 'archived' && (
                                <button className="action-btn archive" onClick={() => handleUpdateStatus('archived')}>
                                    <i className="ph ph-archive"></i>
                                    Archiver
                                </button>
                            )}
                            {selectedMessage?.status !== 'replied' && (
                                <button className="action-btn mark-replied" onClick={() => handleUpdateStatus('replied')}>
                                    <i className="ph ph-check"></i>
                                    Marquer répondu
                                </button>
                            )}
                            <a 
                                href={`mailto:${selectedMessage?.email}?subject=Re: ${selectedMessage?.subject}`}
                                className="action-btn reply"
                            >
                                <i className="ph ph-paper-plane-right"></i>
                                Répondre
                            </a>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Blocked IPs Modal */}
            <Modal show={showBlockedModal} onHide={() => setShowBlockedModal(false)} centered className="message-detail-modal">
                <Modal.Header closeButton>
                    <Modal.Title>IPs bloquées</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleManualBlock} className="manual-block-form">
                        <input
                            type="text"
                            placeholder="Ex: 105.66.12.34"
                            value={manualIp}
                            onChange={(e) => setManualIp(e.target.value)}
                        />
                        <button type="submit" className="ip-btn block" disabled={ipActionLoading || !manualIp.trim()}>
                            <i className="ph ph-prohibit"></i>
                            Bloquer
                        </button>
                    </form>

                    {blockedLoading ? (
                        <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                    ) : blockedIps.length === 0 ? (
                        <p className="blocked-empty">Aucune adresse IP bloquée.</p>
                    ) : (
                        <ul className="blocked-list">
                            {blockedIps.map((item) => (
                                <li key={item.id}>
                                    <div>
                                        <code>{item.ip_address}</code>
                                        <small>{item.reason} · {formatFullDate(item.created_at)}</small>
                                    </div>
                                    <button
                                        className="ip-btn unblock"
                                        onClick={() => unblockIp(item.ip_address)}
                                        disabled={ipActionLoading}
                                    >
                                        <i className="ph ph-lock-open"></i>
                                        Débloquer
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ContactMessages;
